import { motion } from "framer-motion";
import {
  PANEL_STYLES,
  GLASS_INLAY_STYLES,
  GLASS_OPTIONS,
} from "@/lib/catalog";

const JAMBS = [
  { name: "Wood-like Casing", image: "/pdf-embed/jamb-wood-like-casing.png" },
  { name: "Monochromatic Casing", image: "/pdf-embed/jamb-monochromatic-casing.png" },
  { name: "Frameless Jamb", image: "/pdf-embed/jamb-frameless-jamb.png" },
];

const MATERIAL_FINISHES = [
  { name: "Veneer", image: "/pdf-embed/finish-hero-wood-veneer.png" },
  { name: "Melamine", image: "/pdf-embed/finish-hero-melamine.png" },
  { name: "Paint", image: "/pdf-embed/finish-hero-paint.png" },
];

const GLASS_DESCRIPTIONS = {
  Clear: "Transparent glass for unobstructed views.",
  Reeded: "Vertical fluted texture for privacy and style.",
  Obscure: "Textured glass for privacy with light.",
  Frosted: "Etched glass for soft light and privacy.",
  Smoke: "Gray tinted glass for a modern look.",
  Bronze: "Warm tinted glass for a sophisticated feel.",
  Black: "Sleek black glass for maximum privacy.",
};

function SectionEyebrow({ label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-1 h-5 bg-brand-gold" />
      <h3 className="text-brand-green text-xl md:text-2xl uppercase tracking-[0.14em] font-medium">
        {label}
      </h3>
    </div>
  );
}

function DoorTile({ name, image, testid }) {
  return (
    <div className="group text-center" data-testid={testid}>
      <div className="aspect-[2/5] bg-brand-bone overflow-hidden flex items-center justify-center">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
      </div>
      <p className="mt-3 text-[0.72rem] md:text-sm text-brand-ink/85 font-medium tracking-[0.02em]">
        {name}
      </p>
    </div>
  );
}

export default function DoorDesignSection() {
  return (
    <>
      {/* DOOR DESIGN OPTIONS */}
      <section
        id="door-design"
        className="relative bg-brand-bone py-24 md:py-32 overflow-hidden"
        data-testid="door-design-section"
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="text-center mb-4">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="text-brand-green font-light uppercase tracking-[0.06em] text-3xl md:text-4xl lg:text-5xl"
            >
              Door Design Options
            </motion.h2>
            <div className="mx-auto w-24 h-[3px] bg-brand-gold mt-5" />
          </div>
          <p className="max-w-3xl mx-auto text-center text-brand-ink/75 leading-relaxed font-light mb-16">
            Atelier interior doors combine refined craftsmanship with contemporary design. Choose
            from a curated selection of panel styles, inlay details, jamb options and premium
            finishes to create spaces that are modern, elegant and uniquely yours.
          </p>

          {/* Panel Styles */}
          <SectionEyebrow label="Panel Styles" />
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-4 md:gap-6 mt-8 mb-20">
            {PANEL_STYLES.map((p) => (
              <DoorTile
                key={p.name}
                name={p.name}
                image={p.image}
                testid={`panel-style-${p.name.replace(/\W+/g, "-").toLowerCase()}`}
              />
            ))}
          </div>

          {/* Jamb & Casing + Finish Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-24">
            <div>
              <SectionEyebrow label="Jamb & Casing" />
              <div className="grid grid-cols-3 gap-4 mt-8">
                {JAMBS.map((j) => (
                  <div
                    key={j.name}
                    className="text-center group"
                    data-testid={`jamb-${j.name.replace(/\W+/g, "-").toLowerCase()}`}
                  >
                    <div className="aspect-square flex items-center justify-center overflow-hidden bg-brand-bone border border-brand-green/10 group-hover:border-brand-gold transition-colors duration-300 p-3">
                      <img
                        src={j.image}
                        alt={j.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <p className="mt-3 text-[0.72rem] md:text-[0.78rem] text-brand-ink/85 font-medium tracking-[0.02em] group-hover:text-brand-green transition-colors">
                      {j.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <SectionEyebrow label="Finish Options" />
              <div className="grid grid-cols-3 gap-4 mt-8">
                {MATERIAL_FINISHES.map((f) => (
                  <div key={f.name} className="text-center" data-testid={`material-${f.name.toLowerCase()}`}>
                    <div className="aspect-square flex items-center justify-center overflow-hidden">
                      <img src={f.image} alt={f.name} className="w-full h-full object-contain" loading="lazy" />
                    </div>
                    <p className="mt-3 text-[0.72rem] text-brand-ink/85 font-medium">
                      {f.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GLASS INLAY DESIGNS */}
      <section
        id="glass-inlay"
        className="relative bg-brand-bone-2 py-24 md:py-32 overflow-hidden"
        data-testid="glass-inlay-section"
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="text-center mb-4">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="text-brand-green font-light uppercase tracking-[0.06em] text-3xl md:text-4xl lg:text-5xl"
            >
              Glass Inlay Designs
            </motion.h2>
            <div className="mx-auto w-24 h-[3px] bg-brand-gold mt-5" />
          </div>
          <p className="max-w-3xl mx-auto text-center text-brand-ink/75 leading-relaxed font-light mb-16">
            Atelier interior doors with glass inlays bring together natural light and refined design.
            They create a sense of openness while maintaining privacy, enhancing the flow and
            atmosphere of your space. Choose from a variety of panel styles, glass options and
            premium finishes to create a door that&rsquo;s uniquely yours.
          </p>

          {/* Panel Styles with Glass */}
          <SectionEyebrow label="Panel Styles" />
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-4 md:gap-6 mt-8 mb-20">
            {GLASS_INLAY_STYLES.map((g) => (
              <DoorTile
                key={g.name}
                name={g.name}
                image={g.image}
                testid={`glass-panel-${g.name.replace(/\W+/g, "-").toLowerCase()}`}
              />
            ))}
          </div>

          {/* Glass Options */}
          <SectionEyebrow label="Glass Options" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 md:gap-5 mt-8">
            {GLASS_OPTIONS.filter((g) => g.image).map((g) => (
              <div
                key={g.name}
                className="text-left"
                data-testid={`glass-option-${g.name.toLowerCase()}`}
              >
                <div className="aspect-[3/5] flex items-center justify-center overflow-hidden">
                  <img src={g.image} alt={g.name} className="w-full h-full object-contain" loading="lazy" />
                </div>
                <p className="mt-3 text-sm text-brand-ink font-medium">
                  {g.name} Glass
                </p>
                <p className="mt-1 text-xs text-brand-ink/65 leading-relaxed font-light">
                  {GLASS_DESCRIPTIONS[g.name]}
                </p>
              </div>
            ))}
          </div>

          {/* Trust ribbon */}
          <div className="mt-20 border-t border-brand-green/15 pt-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              ["Premium Materials", "Crafted for lasting quality and beauty."],
              ["Custom Solutions", "Designed to fit your space and style."],
              ["Built To Last", "Engineered for durability and everyday performance."],
              ["Timeless Design", "Elevates any interior with understated luxury."],
            ].map(([t, d]) => (
              <div key={t}>
                <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-gold font-medium">
                  ✓ {t}
                </div>
                <p className="mt-2 text-xs text-brand-ink/70 leading-relaxed max-w-[180px] mx-auto">
                  {d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
